import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActivityLogPanel = ({ activityLog, onRefresh }) => {
  const [filterModule, setFilterModule] = useState('all');
  const [filterAccess, setFilterAccess] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const modules = ['all', ...new Set(activityLog?.map(log => log?.module)?.filter(Boolean))];

  const filteredLog = activityLog?.filter(log => {
    const matchesModule = filterModule === 'all' || log?.module === filterModule;
    const matchesAccess = filterAccess === 'all' ||
      (filterAccess === 'granted' && log?.accessGranted) ||
      (filterAccess === 'denied' && !log?.accessGranted);
    const matchesSearch = 
      log?.userEmail?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.department?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    return matchesModule && matchesAccess && matchesSearch;
  }) || [];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    switch (action?.toLowerCase()) {
      case 'read': return 'Eye';
      case 'create': return 'Plus';
      case 'update': return 'Edit';
      case 'delete': return 'Trash2';
      case 'export': return 'Download';
      case 'approve': return 'CheckCircle';
      default: return 'Activity';
    }
  };

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
        <div className="flex flex-wrap gap-2">
          <select
            value={filterModule}
            onChange={(e) => setFilterModule(e?.target?.value)}
            className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {modules?.map(mod => (
              <option key={mod} value={mod}>
                {mod === 'all' ? 'All Modules' : mod}
              </option>
            ))}
          </select>

          <select
            value={filterAccess}
            onChange={(e) => setFilterAccess(e?.target?.value)}
            className="px-3 py-2 border border-input rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Access</option>
            <option value="granted">Granted</option>
            <option value="denied">Denied</option>
          </select>

          <Button variant="outline" size="sm" onClick={onRefresh}>
            <Icon name="RefreshCw" size={14} />
            <span className="ml-2">Refresh</span>
          </Button>
        </div>

        <div className="relative w-full md:w-80">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search activity..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full pl-10 pr-4 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-4 text-sm text-muted-foreground">
        Showing {filteredLog?.length} of {activityLog?.length} activity events
      </div>

      {/* Activity Timeline */}
      <div className="space-y-4">
        {filteredLog?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="Activity" size={48} className="mx-auto mb-2 opacity-50 text-muted-foreground" />
            <p className="text-muted-foreground">No activity found</p>
          </div>
        ) : (
          filteredLog?.map((log) => (
            <div
              key={log?.id}
              className="bg-muted/50 rounded-lg p-4 border border-border hover:border-primary/50 transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  log?.accessGranted ? 'bg-success/10' : 'bg-error/10'
                }`}>
                  <Icon
                    name={getActionIcon(log?.action)}
                    size={18}
                    className={log?.accessGranted ? 'text-success' : 'text-error'}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {log?.userEmail}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {log?.action} on {log?.module} {log?.department && `in ${log?.department}`}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(log?.accessedAt)}
                    </span>
                  </div>

                  {/* Access Status */}
                  <div className="flex items-center gap-2 mt-2">
                    {log?.accessGranted ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success/10 text-success">
                        <Icon name="CheckCircle" size={10} className="mr-1" />
                        Access Granted
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error/10 text-error">
                        <Icon name="XCircle" size={10} className="mr-1" />
                        Access Denied
                      </span>
                    )}

                    {log?.denialReason && (
                      <span className="text-xs text-muted-foreground">
                        Reason: {log?.denialReason}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Export Options */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Export activity log for compliance audits
          </p>
          <Button variant="outline" size="sm">
            <Icon name="Download" size={14} />
            <span className="ml-2">Export CSV</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogPanel;